import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm, FieldValues } from 'react-hook-form';
import { FaMoneyBill } from 'react-icons/fa';
import { MdKeyboardBackspace } from 'react-icons/md';
import { OptionTypeBase } from 'react-select';
import axios from 'axios';
import FileDownload from 'js-file-download';

import { onDelay } from 'utils/debounce';

import { IOptionReactSelect } from 'shared/interfaces';

import { getClientes } from 'services/api/cliente';
import { getFormasPagamento } from 'services/api/formaPagamento';
import { getRelatorioTitulo } from 'services/api/contasReceber';

import { useToast } from 'contexts';
import * as C from 'components';

import { statusTituloOption } from './options';

import * as S from './styles';

import { IRelatorioContasReceberForm } from '../interfaces';

export const ContasReceberPrint: React.FC = () => {
  const { addToast } = useToast();

  const currentDate = new Date();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    control,
  } = useForm<FieldValues>({
    defaultValues: {
      status: { label: 'Abertos', value: 'A' },
      data_vencimento_inicial: new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1,
      ),
      data_vencimento_final: new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0,
      ),
    },
  });

  const [loading, setLoading] = useState(false);

  const loadOptionsFormas = onDelay(
    (
      inputValue: string,
      callback: (options: OptionTypeBase[]) => void,
    ): void => {
      getFormasPagamento({
        page: 1,
        per_page: 15,
        term: inputValue,
      }).then((response) => {
        const values: IOptionReactSelect[] = response.data.map((item) => {
          return {
            label: item.nome,
            value: item.id,
          };
        });

        return callback(values);
      });
    },
    500,
  );

  const loadOptionsClientes = onDelay(
    (
      inputValue: string,
      callback: (options: OptionTypeBase[]) => void,
    ): void => {
      getClientes({
        page: 1,
        per_page: 15,
        term: inputValue,
      }).then((response) => {
        const values: IOptionReactSelect[] = response.data.map((item) => {
          return {
            label: item.nome,
            value: item.id,
          };
        });

        return callback(values);
      });
    },
    500,
  );

  const onSubmit = useCallback(
    async (data: IRelatorioContasReceberForm) => {
      try {
        setLoading(true);

        const response = await getRelatorioTitulo(data);

        if (response.link) {
          axios({
            url: `${response.link}`,
            method: 'GET',
            responseType: 'blob',
          }).then((relatorio) => {
            FileDownload(relatorio.data, 'Titulos contas a receber.pdf');
          });
        }

        addToast({
          type: 'success',
          title: 'Sucesso',
          description: 'Salvo com sucesso',
        });
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Ops, Erro',
          description: 'Ocorreu um erro ao inserir um novo registro',
        });
      } finally {
        setLoading(false);
      }
    },

    [addToast],
  );

  return (
    <S.Container>
      <C.HeaderBackground>
        <S.HeaderPageDatail>
          <S.BackLink>
            <Link to="/contas-receber">
              <MdKeyboardBackspace size={24} color="#fff" />
              Titulos Receber
            </Link>
          </S.BackLink>

          <C.HeaderInfo
            title="Relatorios contas a receber"
            icon={FaMoneyBill}
          />
        </S.HeaderPageDatail>
      </C.HeaderBackground>

      <S.Panel>
        <form onSubmit={handleSubmit(onSubmit)}>
          <S.FormContainer>
            <C.FormGroup>
              <C.Label>Data venda inicial</C.Label>
              <C.InputDatePicker
                {...register('data_venda_inicial')}
                control={control}
                errors={errors.data_venda_inicial}
                dateFormat="dd/MM/yyyy"
                selected={watch('data_venda_inicial')}
                name="data_venda_inicial"
                placeholder="Data venda"
              />
            </C.FormGroup>

            <C.FormGroup>
              <C.Label>Data venda final</C.Label>
              <C.InputDatePicker
                {...register('data_venda_final')}
                control={control}
                errors={errors.data_venda_final}
                dateFormat="dd/MM/yyyy"
                selected={watch('data_venda_final')}
                name="data_venda_final"
                placeholder="Data Compra"
              />
            </C.FormGroup>

            <C.FormGroup>
              <C.Label>Data vencimento inicial</C.Label>
              <C.InputDatePicker
                {...register('data_vencimento_inicial')}
                control={control}
                errors={errors.data_vencimento_inicial}
                dateFormat="dd/MM/yyyy"
                selected={watch('data_vencimento_inicial')}
                name="data_vencimento_inicial"
                placeholder="Data Vencimento"
              />
            </C.FormGroup>

            <C.FormGroup>
              <C.Label>Data vencimento final</C.Label>
              <C.InputDatePicker
                {...register('data_vencimento_final')}
                control={control}
                errors={errors.data_vencimento_final}
                dateFormat="dd/MM/yyyy"
                selected={watch('data_vencimento_final')}
                name="data_vencimento_final"
                placeholder="Data Vencimento"
              />
            </C.FormGroup>

            <C.FormGroup>
              <C.Label>Cliente</C.Label>
              <C.ReactSelectAsync
                {...register('cliente_id')}
                control={control}
                errors={errors.cliente_id}
                loadOptions={loadOptionsClientes}
                isClearable
                isSearchable
                loadingMessage="Buscando..."
                noOptionsMessage="Nenhum cliente localizado"
              />
            </C.FormGroup>

            <C.FormGroup>
              <C.Label>Forma</C.Label>
              <C.ReactSelectAsync
                {...register('forma_pagamento_id')}
                control={control}
                errors={errors.forma_pagamento_id}
                loadOptions={loadOptionsFormas}
                isClearable
                isSearchable
                loadingMessage="Buscando..."
                noOptionsMessage="Nenhuma forma pagamento localizada"
              />
            </C.FormGroup>

            <C.FormGroup>
              <C.Label>Status título</C.Label>
              <C.ReactSelect
                {...register('status')}
                name="status"
                control={control}
                errors={errors.status}
                options={statusTituloOption}
              />
            </C.FormGroup>
          </S.FormContainer>

          <C.Button
            variant="primary"
            disabled={loading}
            loading={loading}
            type="submit"
            title="Gerar relatorio"
          >
            Gerar relatório
          </C.Button>
        </form>
      </S.Panel>
    </S.Container>
  );
};
